import type { Skill } from '../skills'
import type { AvailabilityOptions, ReadableStoryState, Story, StoryNode, WalletStringKey, WalletUpdates } from '../storyAssetTypes'

import { exitRouteTargets } from './exitRoutes'

/** Keys of walletUpdates whose persisted value is a plain string, in author order. */
export const stringWalletKeys = (walletUpdates?: WalletUpdates): WalletStringKey[] =>
  (Object.entries(walletUpdates ?? {}) as [WalletStringKey, WalletUpdates[WalletStringKey]][])
    .filter(([, value]) => typeof value === 'string')
    .map(([key]) => key)

export interface WalletKeyResolution {
  /** The wallet key that supplies the result value and selects the renderer, if resolvable. */
  key?: WalletStringKey;
  /** Authoring problem to surface in dev when no key could be resolved. */
  reason?: string;
}

/**
 * Resolve which walletUpdates entry drives an ending:wallet node.
 *
 * The renderer and value both derive from this single key, so shown-value and
 * saved-value cannot drift. An explicit walletKey is only needed to disambiguate
 * a node that persists more than one string wallet item; otherwise it is inferred
 * from the sole string entry.
 */
export const resolveWalletKey = (
  node?: Pick<StoryNode, 'walletKey' | 'walletUpdates'>,
): WalletKeyResolution => {
  const stringKeys = stringWalletKeys(node?.walletUpdates)
  const explicit = node?.walletKey

  if (explicit) {
    if (!stringKeys.includes(explicit)) {
      return {
        reason: `walletKey "${explicit}" is not a string entry of this node's walletUpdates (${stringKeys.join(', ') || 'none'}).`,
      }
    }
    return { key: explicit }
  }

  if (stringKeys.length === 1) {
    return { key: stringKeys[0] }
  }

  if (stringKeys.length === 0) {
    return { reason: 'ending:wallet node has no string walletUpdates entry to supply a result value.' }
  }

  return {
    reason: `ending:wallet node has multiple string walletUpdates entries (${stringKeys.join(', ')}); set walletKey to disambiguate.`,
  }
}

export const getCurrentNode = (
  nodes: StoryNode[],
  nodeId: string | null,
): StoryNode | null => nodes.find(n => n.id === nodeId) || null

export const evaluateCondition = (
  condition: AvailabilityOptions,
  state: ReadableStoryState,
): boolean => {
  switch (condition.type) {
    case 'default':
      return true

    case 'state_flag':
      return condition.stateFlag
        ? state.stateFlags.includes(condition.stateFlag)
        : false

    case 'visited_node':
      return condition.nodeId
        ? state.visitedNodes.includes(condition.nodeId)
        : false

    default:
      return false
  }
}

export const calculateBadgeProgress = (
  badges: Skill[],
  unlockedIds: string[],
): { earned: number; total: number; percentage: number } => {
  const total = badges.length
  const earned = unlockedIds.filter(id =>
    badges.some(a => a.id === id),
  ).length

  return {
    earned,
    total,
    percentage: total > 0 ? Math.round((earned / total) * 100) : 0,
  }
}

export const getUnlockedSkills = (
  skills: Skill[],
  unlockedIds: string[],
): Skill[] => {
  return skills.filter(a => unlockedIds.includes(a.id))
}

export const getLockedBadges = (
  badges: Skill[],
  unlockedIds: string[],
): Skill[] => {
  return badges.filter(a => !unlockedIds.includes(a.id))
}
export const evaluateBadgeCondition = (
  condition: Skill['unlockCondition'],
  state: ReadableStoryState,
): boolean => {
  switch (condition.type) {
    case 'state_flag':
      return condition.stateFlag
        ? state.stateFlags.includes(condition.stateFlag)
        : false

    case 'multiple_flags': {
      const flags = condition.stateFlags
      const requireAll = condition.requireAll ?? true
      if (!flags || flags.length === 0) {
        return false
      }
      if (requireAll) {
        return flags.every(flag => state.stateFlags.includes(flag))
      }
      return flags.some(flag => state.stateFlags.includes(flag))
    }

    default:
      return false
  }
}

export const END_NODE_TYPES = ['ending', 'ending:rich', 'ending:wallet', 'ending:checklist']

/** End nodes that render an immediate result screen and never play authored dialogue. */
export const IMMEDIATE_END_NODE_TYPES = ['ending:rich', 'ending:wallet', 'ending:checklist']

export const isEndNodeType = (nodeType?: string): boolean =>
  Boolean(nodeType && END_NODE_TYPES.includes(nodeType))

export const calculateLongestPath = (startNodeId: string, nodes: StoryNode[]): number => {
  const nodeMap = new Map(nodes.map(n => [n.id, n]))

  const dfs = (nodeId: string, visited: Set<string>): number => {
    const node = nodeMap.get(nodeId)
    if (!node || visited.has(nodeId)) {
      return 0
    }

    visited.add(nodeId)

    if (isEndNodeType(node.nodeType) || !node.exitRoutes) {
      visited.delete(nodeId)
      return 1
    }

    let max = 0
    for (const value of Object.values(node.exitRoutes)) {
      for (const nextId of exitRouteTargets(value)) {
        max = Math.max(max, dfs(nextId, visited))
      }
    }

    visited.delete(nodeId)
    return 1 + max
  }

  return dfs(startNodeId, new Set())
}

/**
 * Get the reasons that contributed to a skill being unlocked.
 * Returns reasons from the state flags that satisfy the skill's unlock condition.
 */
export const getSkillUnlockReasons = (
  skill: Skill,
  state: ReadableStoryState,
): string[] => {
  const reasons: string[] = []
  const flagReasons = state.stateFlagReasons

  switch (skill.unlockCondition.type) {
    case 'state_flag': {
      const reason = flagReasons[skill.unlockCondition.stateFlag]
      if (reason) {
        reasons.push(reason)
      }
      break
    }
    case 'multiple_flags': {
      for (const flag of skill.unlockCondition.stateFlags) {
        const reason = flagReasons[flag]
        if (reason) {
          reasons.push(reason)
        }
      }
      break
    }
  }

  return reasons
}

/**
 * Check for newly unlocked badges based on current state
 */
export const checkSkills = (
  skills: Skill[],
  currentlyUnlocked: string[],
  state: ReadableStoryState,
): Skill[] => {
  const newlyUnlocked: Skill[] = []

  for (const badge of skills) {
    // Skip if already unlocked
    if (currentlyUnlocked.includes(badge.id)) {
      continue
    }

    // Check if condition is now met
    if (evaluateBadgeCondition(badge.unlockCondition, state)) {
      newlyUnlocked.push(badge)
    }
  }

  return newlyUnlocked
}

/** Story-level sprite framing for the default renderers: an absent key means framed against the disc. */
export const resolveUseClippingAvatars = (story: Pick<Story, 'useClippingAvatars'>): boolean =>
  story.useClippingAvatars !== false
